import React, { useState } from "react";
import environmentSettings from "../../../../constants/environmentSettings";
import Wrapper from "../../components/bankstatement/bankStatement.styles";
import { useOnFairGoMessage, useOnMount, useSetDefaultValue } from "@hooks";
import { AnimatedToggle } from "@components/animatedHeight";
import { useSelector } from "react-redux";
import { useDebounce } from "react-use";
import { isDevelopment } from "@utils/helpers/environment";
import { FIELD_NAMES } from "../../../../constants/commonConstants";
import BankStatementSkipWarning from "../../components/bankstatement/bankStatementSkipWarning";
import { LoaderBackground } from "./loaderBackground";
import PropTypes from "prop-types";

const useIframeHeight = initialIframeHeight => {
  const [iframeHeight, setIframeHeight] = useState(initialIframeHeight);
  const [debouncedIframeHeight, setDebouncedIframeHeight] = useState(initialIframeHeight);
  
  useOnFairGoMessage("Bank_Statements_Resize_Event", value => {
    setIframeHeight(value);
  });
  useDebounce(() => {
    setDebouncedIframeHeight(iframeHeight);
  }, 250, [iframeHeight]);
  
  return debouncedIframeHeight;
};

const useBankStatementDeveloperTools = (props) => {
  useOnMount(() => {
    if (isDevelopment()) {
      window.setBankStatement = () => props.setFieldValue(FIELD_NAMES.BANK_STATEMENT_REFERRAL_CODE, "FBSID-D47C44414DAB4C9ZDpLn3MYTJ");
    }
  });
};

export const BankStatementForm = ({ ...props }) => {
  const { queryTheme, theme: { themeName }, queryString } = useSelector(state => state.StartUp);
  const { initialValue: { illionBrokerFlowDocumentId } } = useSelector(state => state.loanForm);
  const {content, utm_campaign, utm_source, utm_medium} = queryString;

  const [iframeLoading, setIframeLoading] = useState(true);
  const [skipBankStatementsViable, setSkipBankStatementsViable] = useState(false);
  
  useOnFairGoMessage("Bank_Statements_Request_ID",
      value => props.setFieldValue(FIELD_NAMES.BANK_STATEMENT_REFERRAL_CODE, value));
  useOnFairGoMessage("Bank_Statements_Request_Skipped", () => setSkipBankStatementsViable(true));  

  let initialIframeHeight = 560;
  const iframeHeight = useIframeHeight(initialIframeHeight);

  let error = null;
  let classes = ['verifyBankDetails-Container'];
  if(props.errorMessage) {
    error = <p className='validation-message'>{props.errorMessage}</p>;
    classes.push('validation');
  }
  let themeString = "";

  switch (themeName) {
    case "rentBondTheme":
      themeString = "rentbond";
      break;
    case "clearScoreTheme":
      themeString = "clearscore";
      break;
    case "overflowTheme":
      themeString = "overflow";
      break;
    case "yesLoansTheme":
      themeString = "yesloans";
      break;
    case "loanOptionsTheme":
      themeString = "loanoptions";
      break;
    case "nimbleTheme":
    case "zeefiTheme":
      themeString = "nimble";
      break;
    case "fgfTheme":
    case "defaultFallbackTheme":
    default:
      themeString = "fairgofinance";
      break;
  }

  const themeQueryString = "?theme=" + themeString;
  const submitLaterQueryString = "&allowSubmitLater=true";
  const brokerFlowIdQueryString = illionBrokerFlowDocumentId? `&brokerFlowId=${illionBrokerFlowDocumentId}` : "";
  const contentQueryString = "&content=" + (content || ""); 
  let bankStatementIframeUrl = environmentSettings.bankStatementIframeUrl + themeQueryString + contentQueryString + brokerFlowIdQueryString;
  if(props.allowSkip) {
    bankStatementIframeUrl = bankStatementIframeUrl + submitLaterQueryString;
  }
  
  useBankStatementDeveloperTools(props);
  useSetDefaultValue(FIELD_NAMES.BANK_STATEMENT_REFERRAL_CODE);
  useSetDefaultValue(FIELD_NAMES.BANK_STATEMENT_REFERRAL_CODE_SKIP, false);
  useSetDefaultValue(FIELD_NAMES.UTM_VALUES,  { [FIELD_NAMES.UTM_CAMPAIGN]: utm_campaign, [FIELD_NAMES.UTM_SOURCE]: utm_source, [FIELD_NAMES.UTM_MEDIUM]: utm_medium, [FIELD_NAMES.THEME]: queryTheme });

  return (
    <AnimatedToggle show={props.show} initialHeight={initialIframeHeight}>
      <BankStatementSkipWarning
        visible={skipBankStatementsViable}
        close={() => setSkipBankStatementsViable(false)}
        accept={() => {
          setSkipBankStatementsViable(false);
          props.setFieldValue(FIELD_NAMES.BANK_STATEMENT_REFERRAL_CODE_SKIP, true);
        }}
      />
      
      <Wrapper $iframeHeight={iframeHeight} onLoad={() => {setIframeLoading(false);}}>
        <div className={classes.join(' ')}> 
          <iframe
            id="fgf-bank-frame"
            src={bankStatementIframeUrl}
            title="bankFrame"
            scrolling="no"
          />
          {error}
          <LoaderBackground loading={iframeLoading} />
        </div>
      </Wrapper>
    </AnimatedToggle>
  );
};


BankStatementForm.propTypes = {

  setFieldValue : PropTypes.func,
  errorMessage : PropTypes.string,
  allowSkip : PropTypes.bool,
  show: PropTypes.bool
};
